import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { PopoverController } from '@ionic/angular';
import { OverlayEventDetail } from '@ionic/core';
import { copyObject } from '@shared-private/utils/global.utils';
import { first, Subject, takeUntil } from 'rxjs';

import { UsersApiService } from '@core/api-services/users-api/users-api.service';
import { UpdateLanguage, User } from '@core/models';
import { AuthService } from '@core/services/auth/auth.service';
import { TranslationService } from '@core/services/translation/translation.service';
import { WindowMeasuresService } from '@core/services/window-measures/window-measures.service';

import { LanguageSelectPopoverComponent } from '../language-select-popover/language-select-popover.component';

@Component({
  selector: 'el-buen-inquilino-language-select',
  templateUrl: './language-select.component.html',
  standalone: false
})
export class LanguageSelectComponent implements OnInit, OnDestroy {
  currentLang: string;
  @Output() languageChanged = new EventEmitter<string>();
  destroy$ = new Subject<void>();
  windowWidth: number;

  get currentUser(): User {
    return this.authService.user;
  }

  constructor(
    private popoverController: PopoverController,
    private translationService: TranslationService,
    private authService: AuthService,
    private userService: UsersApiService,
    private windowMeasuresService: WindowMeasuresService
  ) {}

  ngOnInit(): void {
    this.getLang();
    this.subscribeToUserChange();
    this.subscribeToWindowWidthChange();
  }

  async openPopover(event: Event): Promise<void> {
    const popover = await this.popoverController.create({
      component: LanguageSelectPopoverComponent,
      event,
      componentProps: { currentLang: this.currentLang }
    });

    popover
      .onDidDismiss()
      .then((resp: OverlayEventDetail<string>) => this.onDismissPopover(resp));
    await popover.present();
  }

  onDismissPopover(resp: OverlayEventDetail<string>): void {
    const { data } = resp;

    if (!data) {
      return;
    }

    this.setCurrentLang(data);

    // Actualizamos el idioma del usuario
    if (this.currentUser) {
      const currentUser = copyObject(this.currentUser);
      currentUser.language = data;
      const updateLanguageDto: UpdateLanguage = {
        language: data
      };
      this.userService
        .updateLanguage(currentUser.id, updateLanguageDto)
        .pipe(first())
        .subscribe({
          next: () => this.onSuccessUpdateUser(currentUser),
          error: (err: HttpErrorResponse) => this.onErrorUpdateUser(err)
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private onSuccessUpdateUser(resp: User): void {
    this.authService.setUser(resp);
  }

  private onErrorUpdateUser(err: HttpErrorResponse): void {
    console.error('error actualizar idioma', err);
  }

  private setCurrentLang(lang: string): void {
    this.currentLang = lang;
    // Cambiamos el idioma de la app
    this.translationService.setAppLanguage(lang);
  }

  private getLang(): void {
    // Obtenemos el idioma de las cookies
    let lang = this.translationService.currentLang;
    const currentUser = this.currentUser;

    // Si ya hay un usuario cargado, miramos sus preferencias guardadas
    if (currentUser && currentUser?.language) {
      lang = currentUser.language;
    }
    this.setCurrentLang(lang);
  }

  private subscribeToUserChange(): void {
    // Obtenemos las preferencias del usuario cada vez que se actualice en el servicio
    this.authService.user$
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp: User) => {
        if (resp && resp?.language) {
          this.setCurrentLang(resp.language);
        }
      });
  }

  private subscribeToWindowWidthChange(): void {
    this.windowMeasuresService
      .getInnerWidth()
      .pipe(takeUntil(this.destroy$))
      .subscribe((width: number) => (this.windowWidth = width));
  }
}
